// ISO-3166 country list with international dialing prefixes
// Used for Address Country and Phone Prefix selectors

export const COUNTRY_PREFIXES = [
  { code: "IT", name: "Italia", prefix: "+39", flag: "\u{1F1EE}\u{1F1F9}" },
  { code: "FR", name: "France", prefix: "+33", flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "ES", name: "Espana", prefix: "+34", flag: "\u{1F1EA}\u{1F1F8}" },
  { code: "DE", name: "Deutschland", prefix: "+49", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "UK", name: "United Kingdom", prefix: "+44", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "PT", name: "Portugal", prefix: "+351", flag: "\u{1F1F5}\u{1F1F9}" },
  { code: "BE", name: "Belgique", prefix: "+32", flag: "\u{1F1E7}\u{1F1EA}" },
  { code: "NL", name: "Nederland", prefix: "+31", flag: "\u{1F1F3}\u{1F1F1}" },
  { code: "PL", name: "Polska", prefix: "+48", flag: "\u{1F1F5}\u{1F1F1}" },
  { code: "SE", name: "Sverige", prefix: "+46", flag: "\u{1F1F8}\u{1F1EA}" },
  { code: "NO", name: "Norge", prefix: "+47", flag: "\u{1F1F3}\u{1F1F4}" },
  { code: "FI", name: "Suomi", prefix: "+358", flag: "\u{1F1EB}\u{1F1EE}" },
  { code: "DK", name: "Danmark", prefix: "+45", flag: "\u{1F1E9}\u{1F1F0}" },
  { code: "CZ", name: "Ceska", prefix: "+420", flag: "\u{1F1E8}\u{1F1FF}" },
  { code: "GR", name: "Ellada", prefix: "+30", flag: "\u{1F1EC}\u{1F1F7}" },
  { code: "CH", name: "Schweiz", prefix: "+41", flag: "\u{1F1E8}\u{1F1ED}" },
  { code: "GBZ", name: "Gibraltar", prefix: "+350", flag: "\u{1F1EC}\u{1F1EE}" },
  { code: "IRL", name: "Ireland", prefix: "+353", flag: "\u{1F1EE}\u{1F1EA}" },
  { code: "AT", name: "Osterreich", prefix: "+43", flag: "\u{1F1E6}\u{1F1F9}" },
  { code: "LU", name: "Luxembourg", prefix: "+352", flag: "\u{1F1F1}\u{1F1FA}" },
  { code: "RO", name: "Romania", prefix: "+40", flag: "\u{1F1F7}\u{1F1F4}" },
  { code: "HU", name: "Magyarorszag", prefix: "+36", flag: "\u{1F1ED}\u{1F1FA}" },
  { code: "SK", name: "Slovensko", prefix: "+421", flag: "\u{1F1F8}\u{1F1F0}" },
  { code: "SI", name: "Slovenija", prefix: "+386", flag: "\u{1F1F8}\u{1F1EE}" },
  { code: "HR", name: "Hrvatska", prefix: "+385", flag: "\u{1F1ED}\u{1F1F7}" },
  { code: "BG", name: "Balgariya", prefix: "+359", flag: "\u{1F1E7}\u{1F1EC}" },
  { code: "EE", name: "Eesti", prefix: "+372", flag: "\u{1F1EA}\u{1F1EA}" },
  { code: "LV", name: "Latvija", prefix: "+371", flag: "\u{1F1F1}\u{1F1FB}" },
  { code: "LT", name: "Lietuva", prefix: "+370", flag: "\u{1F1F1}\u{1F1F9}" },
  { code: "MT", name: "Malta", prefix: "+356", flag: "\u{1F1F2}\u{1F1F9}" },
  { code: "CY", name: "Kypros", prefix: "+357", flag: "\u{1F1E8}\u{1F1FE}" },
  { code: "IS", name: "Island", prefix: "+354", flag: "\u{1F1EE}\u{1F1F8}" },
  { code: "US", name: "United States", prefix: "+1", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "CA", name: "Canada", prefix: "+1", flag: "\u{1F1E8}\u{1F1E6}" },
  { code: "AU", name: "Australia", prefix: "+61", flag: "\u{1F1E6}\u{1F1FA}" },
];

// ── ISO-3166-1 → legacy LoJack code mapping ──────────────────────────
//
// Fabrizio bug 06/07/2026 — Il DB dei dealer/customer salva il ``country``
// come codice ISO standard (GB, IE, GI, ecc.) ma la ``COUNTRY_PREFIXES``
// storicamente usa i codici legacy LoJack (UK, IRL, GBZ). Senza questa
// normalizzazione, ``getPrefixByCountry("GB")`` non trova match e fa
// fallback silente a "+39", con il risultato che un dealer inglese si
// vede proporre l'Italia come prefisso di default (idem Ireland,
// Gibraltar).
//
// ``normalizeCountryCode`` accetta ENTRAMBE le forme e ritorna sempre il
// codice interno usato da ``COUNTRY_PREFIXES`` (UK/GBZ/IRL). Chiamare
// questa funzione ovunque un ``code`` esterno (dealer.country,
// customer.country, ecc.) sia passato ad una lookup basata su
// ``COUNTRY_PREFIXES``.
const ISO_TO_LOJACK = {
  GB: "UK",
  GI: "GBZ",
  IE: "IRL",
};

export const normalizeCountryCode = (code) => {
  if (!code) return code;
  const up = String(code).toUpperCase();
  return ISO_TO_LOJACK[up] || up;
};

export const getPrefixByCountry = (code) =>
  COUNTRY_PREFIXES.find((c) => c.code === normalizeCountryCode(code))?.prefix || "+39";

export const getCountryByCode = (code) =>
  COUNTRY_PREFIXES.find((c) => c.code === normalizeCountryCode(code)) || COUNTRY_PREFIXES[0];

// Reverse lookup: dial-prefix → country code. Returns the FIRST match in
// COUNTRY_PREFIXES order, which intentionally puts the 18 LoJack-served
// countries before others (so e.g. "+1" maps to US/CA in that order — we
// don't auto-correct to those non-LoJack markets anyway). Returns null if
// no match.
export const getCountryByPrefix = (prefix) => {
  if (!prefix) return null;
  const normalized = String(prefix).trim();
  return COUNTRY_PREFIXES.find((c) => c.prefix === normalized)?.code || null;
};

// ── Country-name localization (req. A. Zorzutti — Stand-up 04/06/2026 pt. 5):
// Returns the country name **translated into the UI language**, not the
// endonym. Example: when the UI language is "IT", "UK" returns "Regno Unito"
// (not "United Kingdom"). Falls back to the static endonym when Intl is not
// available or the locale/code is unsupported.
//
// Maps the internal LoJack country codes (which use UK/GBZ/IRL for legacy
// reasons) to the ISO-3166-1 alpha-2 codes that Intl.DisplayNames expects.
const LOJACK_TO_ISO = {
  UK: "GB",
  GBZ: "GI",
  IRL: "IE",
  CZ: "CZ",
  GR: "GR",
};

// LoJack UI languages → BCP-47 locale tags accepted by Intl.DisplayNames
const UI_LANG_TO_LOCALE = {
  IT: "it", UK: "en", FR: "fr", ES: "es", DE: "de", NL: "nl", PT: "pt",
  PL: "pl", SE: "sv", NO: "no", FI: "fi", DK: "da", CZ: "cs", GR: "el",
};

let _displayNamesCache = {};
const _getDisplayNames = (langCode) => {
  const locale = UI_LANG_TO_LOCALE[langCode] || UI_LANG_TO_LOCALE.UK;
  if (_displayNamesCache[locale]) return _displayNamesCache[locale];
  try {
    const dn = new Intl.DisplayNames([locale], { type: "region" });
    _displayNamesCache[locale] = dn;
    return dn;
  } catch (_err) {
    return null;
  }
};

export const getLocalizedCountryName = (countryCode, uiLang) => {
  if (!countryCode) return "";
  const iso = LOJACK_TO_ISO[countryCode] || countryCode;
  const dn = _getDisplayNames(uiLang);
  if (dn) {
    try {
      const name = dn.of(iso);
      if (name && name !== iso) return name;
    } catch (_err) {
      /* fall through to endonym */
    }
  }
  // Fallback to static endonym
  return COUNTRY_PREFIXES.find((c) => c.code === normalizeCountryCode(countryCode))?.name || countryCode;
};

// ── Date formatting per UI language (req. A. Zorzutti pt. 6):
// Formats an ISO-8601 date string into the conventional format of the
// active UI language. Defaults to dd/MM/yyyy (Italian) when unknown.
//
// Examples:
//   formatLocalizedDate("2026-06-04", "IT")  → "04/06/2026"
//   formatLocalizedDate("2026-06-04", "UK")  → "04/06/2026"  (UK = dd/mm/yyyy)
//   formatLocalizedDate("2026-06-04", "DE")  → "04.06.2026"
//   formatLocalizedDate("2026-06-04", "SE")  → "2026-06-04"
export const formatLocalizedDate = (isoDate, uiLang, opts) => {
  if (!isoDate) return "";
  const d = isoDate instanceof Date ? isoDate : new Date(isoDate);
  if (isNaN(d.getTime())) return String(isoDate);
  const locale = UI_LANG_TO_LOCALE[uiLang] || "en-GB";
  try {
    return new Intl.DateTimeFormat(
      locale === "en" ? "en-GB" : locale,
      opts || { day: "2-digit", month: "2-digit", year: "numeric" },
    ).format(d);
  } catch (_err) {
    // Manual fallback: dd/MM/yyyy
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  }
};
